import type {
  AdminProfile,
  ApiErrorBody,
  ArticleDetail,
  ArticleListItem,
  ArticleListQuery,
  Category,
  ChangePasswordRequest,
  CreateArticleRequest,
  CreateCategoryRequest,
  CreateTagRequest,
  DashboardData,
  LoginRequest,
  PageResult,
  SiteInfo,
  Tag,
  UpdateArticleRequest,
  UpdateCategoryRequest,
  UpdateTagRequest,
  UploadImageResponse,
} from './types'

export class ApiError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, body: ApiErrorBody) {
    super(body.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
  }
}

let unauthorizedHandler: (() => void) | undefined

export function setUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  json?: unknown
  body?: BodyInit
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers()
  let body = options.body
  if (options.json !== undefined) {
    headers.set('Content-Type', 'application/json')
    body = JSON.stringify(options.json)
  }
  const init: RequestInit = {
    method: options.method ?? 'GET',
    credentials: 'same-origin',
    headers,
  }
  if (body !== undefined) init.body = body
  const response = await fetch(`/api/v1${path}`, init)
  if (!response.ok) {
    const payload: ApiErrorBody = response.headers.get('content-type')?.includes('application/json')
      ? await response.json() as ApiErrorBody
      : { code: 'http_error', message: response.status === 413 ? '上传内容超过服务器大小限制' : `请求失败（HTTP ${response.status}）` }
    if (response.status === 401) {
      unauthorizedHandler?.()
    }
    throw new ApiError(response.status, payload)
  }
  if (response.status === 204) {
    return undefined as T
  }
  return (await response.json()) as T
}

function queryString(query: ArticleListQuery): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') {
      params.set(key, String(value))
    }
  }
  const serialized = params.toString()
  return serialized ? `?${serialized}` : ''
}

export const siteApi = {
  get: (): Promise<SiteInfo> => request('/site'),
}

export const publicApi = {
  listArticles: (query: ArticleListQuery): Promise<PageResult<ArticleListItem>> => request(`/articles${queryString(query)}`),
  getArticle: (slug: string): Promise<ArticleDetail> => request(`/articles/${encodeURIComponent(slug)}`),
  listCategories: (): Promise<Category[]> => request('/categories'),
  listTags: (): Promise<Tag[]> => request('/tags'),
}

export const authApi = {
  login: (payload: LoginRequest): Promise<AdminProfile> => request('/admin/session', { method: 'POST', json: payload }),
  logout: (): Promise<void> => request('/admin/session', { method: 'DELETE' }),
  me: (): Promise<AdminProfile> => request('/admin/me'),
  changePassword: (payload: ChangePasswordRequest): Promise<void> => request('/admin/password', { method: 'PUT', json: payload }),
}

export const studioApi = {
  dashboard: (): Promise<DashboardData> => request('/admin/dashboard'),
  listArticles: (query: ArticleListQuery): Promise<PageResult<ArticleListItem>> => request(`/admin/articles${queryString(query)}`),
  getArticle: (id: number): Promise<ArticleDetail> => request(`/admin/articles/${id}`),
  createArticle: (payload: CreateArticleRequest): Promise<ArticleDetail> => request('/admin/articles', { method: 'POST', json: payload }),
  updateArticle: (id: number, payload: UpdateArticleRequest): Promise<ArticleDetail> => request(`/admin/articles/${id}`, { method: 'PUT', json: payload }),
  deleteArticle: (id: number): Promise<void> => request(`/admin/articles/${id}`, { method: 'DELETE' }),
  listCategories: (): Promise<Category[]> => request('/admin/categories'),
  createCategory: (payload: CreateCategoryRequest): Promise<Category> => request('/admin/categories', { method: 'POST', json: payload }),
  updateCategory: (id: number, payload: UpdateCategoryRequest): Promise<Category> => request(`/admin/categories/${id}`, { method: 'PUT', json: payload }),
  deleteCategory: (id: number): Promise<void> => request(`/admin/categories/${id}`, { method: 'DELETE' }),
  listTags: (): Promise<Tag[]> => request('/admin/tags'),
  createTag: (payload: CreateTagRequest): Promise<Tag> => request('/admin/tags', { method: 'POST', json: payload }),
  updateTag: (id: number, payload: UpdateTagRequest): Promise<Tag> => request(`/admin/tags/${id}`, { method: 'PUT', json: payload }),
  deleteTag: (id: number): Promise<void> => request(`/admin/tags/${id}`, { method: 'DELETE' }),
  uploadImage: (file: File): Promise<UploadImageResponse> => {
    const form = new FormData()
    form.append('file', file)
    return request('/admin/uploads', { method: 'POST', body: form })
  },
}
